'use client';

import type { CSSProperties, ReactElement } from 'react';
import React from 'react';
import { Rating } from '@lmn/design-system';
import { useTheme } from 'styled-components';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { StarsContainer } from '@/app/common/components/Stars/styled.ts';
import { useTranslation } from '@/app/components/translations/Translation';
import { isGermany } from './utils.ts';

type StarsProps = {
  stars: number;
  style?: CSSProperties;
  className?: string;
  black?: boolean;
  color?: CSSProperties['color'];
  maxRating?: number;
  hideEmpty?: boolean;
  decorative?: boolean;
  size?: number;
};

const Stars = ({
  stars,
  style = {},
  black = false,
  color,
  maxRating,
  hideEmpty,
  decorative = false,
  size,
  ...rest
}: StarsProps): ReactElement | null => {
  const { t } = useTranslation();
  const { localization } = usePageWrapperData();
  const theme = useTheme();
  const isDE = isGermany(localization);
  const ratingLabel =
    stars === 1
      ? t('vacation.oss.filters.stars.placeholder_singular')
      : t('vacation.oss.filters.stars.placeholder', {
          number: stars.toString(),
        });

  return stars > 0 ? (
    // decorative: the glyph repeats adjacent text, so keep it out of the accessibility tree.
    <StarsContainer {...rest} style={style} aria-hidden={decorative || undefined}>
      <Rating
        rating={stars}
        type={isDE ? 'SUNS' : 'STARS'}
        color={color ?? (black ? undefined : theme.kallisto.color.common.semantic.warning[400])}
        maxRating={maxRating}
        hideEmpty={hideEmpty}
        size={size}
        aria-label={ratingLabel}
      />
    </StarsContainer>
  ) : null;
};

export default Stars;
